import { UiResult } from '@/shared/ui'
import { HomeOutlined as Home, RotateCcwOutlined as RotateCcw } from '@/shared/ui/icons'
import UiButton from '@/shared/ui/ui-button'
import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught runtime error:', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
    window.location.reload()
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: undefined })
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px 16px', maxWidth: 800, margin: '0 auto' }}>
          <UiResult
            status="error"
            title="Đã xảy ra lỗi không mong muốn"
            subTitle="Giao diện gặp sự cố khi xử lý dữ liệu. Bạn có thể tải lại trang hoặc quay về trang chủ."
            extra={
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <UiButton icon={<Home size={16} />} onClick={this.handleGoHome}>
                  Về trang chủ
                </UiButton>
                <UiButton type="primary" icon={<RotateCcw size={16} />} onClick={this.handleReset}>
                  Tải lại trang
                </UiButton>
              </div>
            }
          />
          {import.meta.env.DEV && this.state.error && (
            <div
              style={{
                marginTop: 24,
                padding: 16,
                background: 'var(--vbdt-color-status-error-bg)',
                border:
                  '1px solid color-mix(in srgb, var(--vbdt-color-status-error) 40%, transparent)',
                borderRadius: 8,
                fontSize: 13,
                fontFamily: 'monospace',
                overflowX: 'auto',
                color: 'var(--vbdt-color-status-error)',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                [DEV ONLY] {this.state.error.name}: {this.state.error.message}
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {this.state.error.stack}
              </pre>
            </div>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
